/**
 * The Limit Explanation of a Limited Recommendation.
 *
 * The processor writes `PriceRecommendationState.narrative` when a bound of the pricing
 * strategy clamps the master-room recommendation (PriceRecommendationStateEffectiveRateCalculator
 * .buildAdjustmentNarrative). The string is a contract with a fixed vocabulary:
 *
 *   Suggested: +4% (177 -> 184) | Applied: -24% (135) | Limit: Max 135 | Reason: Above max
 *   | Rule: Highest comp -10% | Base: Grand Hyatt Athens (150)
 *   [| Threshold: 800 used instead of 274[; only 1 competitor priced]]
 *   [| Threshold: 800 used; no competitor data]
 *   Rule: Fixed price  (a STATIC rule has no Base and no Threshold)
 *
 * The amounts are on the scale of the Hotel Market Price (the market rate of the hotel on
 * the channel of the recommendation), not on the PMS scale of the room states.
 *
 * Repeated `Room limit` parts describe room-level clamps and are excluded from this explanation.
 * `parsePriceRecommendationLimit` turns the string into a typed object, or `undefined` for
 * an unknown shape (the UI then shows the stored text as it is).
 * `describePriceRecommendationLimit` writes the two lines of the UI.
 */

export type PriceRecommendationLimitBound = 'MAX' | 'MIN';

export type PriceRecommendationLimitEvaluate = 'HIGHEST' | 'LOWEST' | 'MEDIAN';

export type PriceRecommendationLimitRule =
	| { type: 'FIXED' }
	| { type: 'DYNAMIC', evaluate: PriceRecommendationLimitEvaluate, percentage: number };

export type PriceRecommendationLimitThreshold = {
	amount: number,
	// The value the rule computed before the threshold replaced it. Absent when no competitor had a price.
	insteadOf?: number,
	noCompetitorData: boolean,
	// ROO2-66: the count of priced competitors when it was below the minimum of the rule.
	competitorsPriced?: number,
};

export type PriceRecommendationLimit = {
	// The percent the engine suggested, as the processor rounded it (whole percent).
	suggestedRate: number,
	marketPrice: number,
	suggestedPrice: number,
	// The percent the bound applied on the market price (whole percent).
	appliedRate: number,
	bound: PriceRecommendationLimitBound,
	boundPrice: number,
	rule: PriceRecommendationLimitRule,
	// The competitor price the dynamic rule took (the room mapped to the type of the architecture).
	base?: { name: string, price: number },
	threshold?: PriceRecommendationLimitThreshold,
};

const PART_SEPARATOR = '|';

const isRoomLimitPart = (part: string): boolean => {
	const separatorIndex = part.indexOf(':');
	return separatorIndex !== -1 && part.slice(0, separatorIndex).trim() === 'Room limit';
};

// Room-level clamps are recorded separately from the master-rule explanation.
// Preserve all other parts, including unknown text used by the fallback.
export const removeRoomLimitParts = (narrative: string | null | undefined): string | null | undefined =>
	narrative?.split(PART_SEPARATOR).filter(part => !isRoomLimitPart(part)).join(PART_SEPARATOR);

const toParts = (narrative: string): Map<string, string> | undefined => {
	const parts = new Map<string, string>();
	for (const part of narrative.split(PART_SEPARATOR)) {
		if (isRoomLimitPart(part)) continue;
		const separatorIndex = part.indexOf(':');
		if (separatorIndex === -1) return undefined;
		const label = part.slice(0, separatorIndex).trim();
		const value = part.slice(separatorIndex + 1).trim();
		if (!['Suggested', 'Applied', 'Limit', 'Reason', 'Rule', 'Base', 'Threshold'].includes(label)
			|| value.length === 0 || parts.has(label)) return undefined;
		parts.set(label, value);
	}
	return parts;
};

const RATE = '([+-]?\\d+)%';
const AMOUNT = '(\\d+)';

const parseRule = (value: string): PriceRecommendationLimitRule | undefined => {
	if (value === 'Fixed price') return { type: 'FIXED' };
	const match = new RegExp(`^(Highest|Lowest|Median) comp ${RATE}$`).exec(value);
	if (match === null) return undefined;
	const evaluate: PriceRecommendationLimitEvaluate = match[1] === 'Highest' ? 'HIGHEST' : match[1] === 'Lowest' ? 'LOWEST' : 'MEDIAN';
	return { type: 'DYNAMIC', evaluate, percentage: Number(match[2]) };
};

const parseBase = (value: string | undefined): { name: string, price: number } | undefined => {
	if (value === undefined) return undefined;
	const match = new RegExp(`^(.+) \\(${AMOUNT}\\)$`).exec(value);
	if (match === null) return undefined;
	return { name: match[1].trim(), price: Number(match[2]) };
};

const parseThreshold = (value: string | undefined): PriceRecommendationLimitThreshold | undefined | null => {
	if (value === undefined) return undefined;
	const noData = new RegExp(`^${AMOUNT} used; no competitor data$`).exec(value);
	if (noData !== null) return { amount: Number(noData[1]), noCompetitorData: true };
	const insteadOf = new RegExp(`^${AMOUNT} used instead of ${AMOUNT}(?:; only (\\d+) competitors? priced)?$`).exec(value);
	if (insteadOf === null) return null;
	return {
		amount: Number(insteadOf[1]),
		insteadOf: Number(insteadOf[2]),
		noCompetitorData: false,
		competitorsPriced: insteadOf[3] === undefined ? undefined : Number(insteadOf[3]),
	};
};

export const parsePriceRecommendationLimit = (narrative: string | null | undefined): PriceRecommendationLimit | undefined => {
	if (narrative === null || narrative === undefined || narrative.trim().length === 0) return undefined;
	const parts = toParts(narrative);
	if (parts === undefined) return undefined;

	const suggested = new RegExp(`^${RATE} \\(${AMOUNT} -> ${AMOUNT}\\)$`).exec(parts.get('Suggested') ?? '');
	const applied = new RegExp(`^${RATE} \\(${AMOUNT}\\)$`).exec(parts.get('Applied') ?? '');
	const limit = new RegExp(`^(Max|Min) ${AMOUNT}$`).exec(parts.get('Limit') ?? '');
	const rule = parseRule(parts.get('Rule') ?? '');
	const threshold = parseThreshold(parts.get('Threshold'));
	const base = parseBase(parts.get('Base'));
	if (parts.has('Base') && base === undefined) return undefined;
	if (suggested === null || applied === null || limit === null || rule === undefined || threshold === null) return undefined;

	return {
		suggestedRate: Number(suggested[1]),
		marketPrice: Number(suggested[2]),
		suggestedPrice: Number(suggested[3]),
		appliedRate: Number(applied[1]),
		bound: limit[1] === 'Max' ? 'MAX' : 'MIN',
		boundPrice: Number(limit[2]),
		rule,
		base,
		threshold,
	};
};

export type PriceRecommendationLimitText = {
	// The tag of the day popup ("Max rule" / "Min rule") and of the drawer ("Set by your max rule" / "Set by your min rule").
	tag: string,
	tagShort: string,
	// The title of the hover card.
	title: string,
	// Line 1: the percent the rule set. The engine percent follows it, muted, for information.
	ruleLine: string,
	engineLine: string,
	// Line 2: the cap or the floor and its source. No market rate (decision 10 of 2026-09-08).
	detailLine: string,
};

export type PriceRecommendationLimitTextOptions = {
	formatAmount: (amount: number) => string,
	// The room type of the architecture, as the UI names it ("Premium").
	roomTypeLabel: string,
};

const MINUS = '−';
const DOT = ' · ';

const formatRate = (rate: number): string => rate < 0 ? `${MINUS}${Math.abs(rate)}%` : rate > 0 ? `+${rate}%` : '0%';

const formatStep = (percentage: number): string => {
	if (percentage === 0) return '';
	return percentage < 0 ? ` ${MINUS} ${Math.abs(percentage)}%` : ` + ${percentage}%`;
};

const EVALUATE_WORDS: Record<PriceRecommendationLimitEvaluate, string> = {
	HIGHEST: 'the most expensive',
	LOWEST: 'the least expensive',
	MEDIAN: 'the median',
};

export const describePriceRecommendationLimit = (limit: PriceRecommendationLimit, options: PriceRecommendationLimitTextOptions): PriceRecommendationLimitText => {
	const { formatAmount, roomTypeLabel } = options;
	const isMax = limit.bound === 'MAX';
	const boundWord = isMax ? 'Market-rate cap' : 'Market-rate floor';

	const source = (): string => {
		if (limit.base !== undefined) {
			return `${limit.base.name} (${roomTypeLabel}) ${formatAmount(limit.base.price)}${limit.rule.type === 'DYNAMIC' ? formatStep(limit.rule.percentage) : ''}`;
		}
		if (limit.rule.type === 'DYNAMIC') {
			return `${EVALUATE_WORDS[limit.rule.evaluate]} ${roomTypeLabel} room of your competitors${formatStep(limit.rule.percentage)}`;
		}
		return 'fixed';
	};

	let detailLine: string;
	if (limit.rule.type === 'FIXED') {
		detailLine = `${boundWord} ${formatAmount(limit.boundPrice)}, fixed`;
	} else if (limit.threshold !== undefined && limit.threshold.noCompetitorData) {
		detailLine = `${boundWord} ${formatAmount(limit.threshold.amount)} = your manual threshold${DOT}No competitor had a price`;
	} else if (limit.threshold !== undefined && limit.threshold.competitorsPriced !== undefined) {
		const count = limit.threshold.competitorsPriced;
		const computed = limit.threshold.insteadOf !== undefined ? ` = ${formatAmount(limit.threshold.insteadOf)}` : '';
		detailLine = `${boundWord} ${formatAmount(limit.threshold.amount)} = your manual threshold${DOT}Only ${count} competitor${count === 1 ? '' : 's'} priced (${source()}${computed})`;
	} else if (limit.threshold !== undefined) {
		const computed = limit.threshold.insteadOf !== undefined ? formatAmount(limit.threshold.insteadOf) : formatAmount(limit.boundPrice);
		detailLine = `${boundWord} ${formatAmount(limit.threshold.amount)} = your manual threshold${DOT}The rule gave ${computed} (${source()})`;
	} else {
		detailLine = `${boundWord} ${formatAmount(limit.boundPrice)} = ${source()}`;
	}

	return {
		tag: isMax ? 'Set by your max rule' : 'Set by your min rule',
		tagShort: isMax ? 'Max rule' : 'Min rule',
		title: isMax ? 'Set by your maximum price rule' : 'Set by your minimum price rule',
		ruleLine: `Your rule ${formatRate(limit.appliedRate)}`,
		engineLine: `engine ${formatRate(limit.suggestedRate)}`,
		detailLine,
	};
};
